import { ApiClient } from '@center-technologies/shared-api-clients';
import { BasicSphereInfo } from 'types/global';

export interface GetBasicSphereInfoParams {
  sphereId: string;
  skipCache?: boolean;
}

/**
 * Retrieves basic sphere information from the graph service.
 *
 * @param apiClient – An initialized ApiClient pointed at your Graph endpoint.
 * @param params – Parameters including sphereId and optional skipCache flag.
 * @returns An object with success, statusCode, message, and data fields.
 * @throws Error object with { success, statusCode, message, response } on failure.
 */
export async function getBasicSphereInfo(
  apiClient: ApiClient,
  params: GetBasicSphereInfoParams
): Promise<{
  success: boolean;
  statusCode: number;
  message: string;
  data?: BasicSphereInfo;
}> {
  const { sphereId, skipCache = false } = params;

  if (!sphereId?.trim()) {
    throw {
      success: false,
      statusCode: 400,
      message: 'getBasicSphereInfo: sphereId is required',
      response: null,
    };
  }

  // Build URL path and query string
  const queryParams = new URLSearchParams();
  if (skipCache) {
    queryParams.set('skipCache', 'true');
  }
  const queryString = queryParams.toString();
  const path = `/spheres/${encodeURIComponent(sphereId)}/info${queryString ? `?${queryString}` : ''}`;

  try {
    // Use apiClient.get to fetch sphere info
    const response = await apiClient.get<{ sphere: BasicSphereInfo }>(path);
    return {
      success: true,
      statusCode: 200,
      message: 'Basic sphere info retrieved successfully',
      data: response.sphere,
    };
  } catch (err: any) {
    console.error(`getBasicSphereInfo error for sphereId=${sphereId}:`, err);

    // If AxiosError-like structure, err.response may exist
    const status = err.response?.status ?? 500;
    const errorMsg =
      err.response?.data?.error ?? err.message ?? 'Failed to retrieve basic sphere info';

    throw {
      success: false,
      statusCode: status,
      message: errorMsg,
      response: err.response ?? { status, data: { error: errorMsg } },
    };
  }
}